class CustomerSubscriptionService {
  static cache = {};
  static requests = {};
  static notifyOnce = {};

  // --- helper: timeout wrapper to avoid hanging fetches
  static async fetchWithTimeout(url, options = {}, timeoutMs = 8000) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * Fetches customer subscription data from the existing /a/sub app proxy
   */
  static async getCustomerSubscription(customerId, shop) {
    if (!customerId || !shop) {
      return null;
    }

    const cacheKey = `${shop}-${customerId}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];
    if (this.requests[cacheKey]) return this.requests[cacheKey];

    const apiUrl = `/a/sub/plan-info`;

    this.requests[cacheKey] = (async () => {
      try {
        const response = await this.fetchWithTimeout(apiUrl, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            console.warn('�� Not authorized for subscription info');
            return { planStatus: 'UNAVAILABLE' };
          }
          console.warn('❌ Failed to fetch subscription data:', response.status);
          return null;
        }

        let data = null;
        try {
          data = await response.json();
        } catch {
          console.warn('❌ Non-JSON response from app proxy');
          return null;
        }

        this.cache[cacheKey] = data;
        return data;
      } catch (error) {
        if (error.name === 'AbortError') console.warn('⏱️ App proxy request timed out');
        else console.error('💥 Error fetching customer subscription:', error);
        return null;
      } finally {
        delete this.requests[cacheKey];
      }
    })();

    return this.requests[cacheKey];
  }

  /**
   * Updates membership plan CTAs based on customer subscription data
   * Dynamically extracts plan information from various data structures
   */
  static updateMembershipPlanCTAs(planData) {
    console.log('planData', planData);
    if (!planData) {
      // User has no subscription - keep "Get started" quiz links (default from Liquid)
      return;
    }
console.log('move forward update')
    // User has subscription - convert quiz links to "Switch plan" buttons
    this.convertToSwitchButtons();
    
    // Extract plan information dynamically from the data
    const planInfo = this.extractPlanInfoFromData(planData);
    const currentPlanKey = planInfo.planKey;
    const planStatus = planInfo.planStatus;

    // Store the current box configuration for comparison
    this.currentBoxConfiguration = planData.boxConfiguration;

    this.updatePlanCards(currentPlanKey, planStatus);
    this.updateMembershipTable(currentPlanKey, planStatus);
  }

  /**
   * Dynamically extracts plan information by analyzing the data structure
   * Uses keyword detection to determine plan type
   */
  static extractPlanInfoFromData(planData) {
    // Try to find plan name from various possible fields
    const planName = this.findPlanName(planData);
    
    // Try to find plan key/slug from various possible fields
    const planKey = this.findPlanKey(planData);
    
    // Try to find plan status from various possible fields
    const planStatus = this.findPlanStatus(planData);
    
    // Determine the final plan key using intelligent keyword detection
    const finalPlanKey = this.determinePlanKeyFromKeywords(planName, planKey);
    
    return {
      planKey: finalPlanKey,
      planStatus: planStatus,
      planName: planName,
      originalPlanKey: planKey
    };
  }

  /**
   * Finds plan name from various possible field names
   */
  static findPlanName(planData) {
    const possibleFields = ['planName', 'plan_name', 'name', 'title', 'planTitle'];
    
    for (const field of possibleFields) {
      if (planData[field] && typeof planData[field] === 'string') {
        return planData[field];
      }
    }
    
    return null;
  }

  /**
   * Finds plan key/slug from various possible field names
   */
  static findPlanKey(planData) {
    const possibleFields = ['planKey', 'plan_key', 'plan_slug', 'slug', 'key', 'id'];
    
    for (const field of possibleFields) {
      if (planData[field] && typeof planData[field] === 'string') {
        return planData[field];
      }
    }
    
    return null;
  }

  /**
   * Finds plan status from various possible field names
   */
  static findPlanStatus(planData) {
    const possibleFields = ['planStatus', 'plan_status', 'status', 'state'];
    
    for (const field of possibleFields) {
      if (planData[field] && typeof planData[field] === 'string') {
        return planData[field];
      }
    }
    
    return 'ACTIVE'; // Default fallback
  }

  /**
   * Centralized plan patterns for consistent matching across the application
   */
  static getPlanPatterns() {
    return {
      'classic': ['classic', 'classic-plan', 'classin_plan'],
      'preferred': ['preferred', 'preferred-plan', 'preferred_plan'],
      'premier': ['premier', 'premier-plan', 'premier_plan'],
      'fine_wine': ['fine-wine', 'fine_wine', 'fine-wine-plan', 'fine_wine_plan',]
    };
  }

  /**
   * Determines plan key by analyzing keywords in plan name or key
   */
  static determinePlanKeyFromKeywords(planName, planKey) {
    // Combine both name and key for analysis
    const textToAnalyze = [planName, planKey].filter(Boolean).join(' ').toLowerCase();
    const planPatterns = this.getPlanPatterns();
    
    // Find the best matching plan based on keywords
    for (const [planType, keywords] of Object.entries(planPatterns)) {
      for (const keyword of keywords) {
        if (textToAnalyze.includes(keyword)) {
          return planType;
        }
      }
    }
    
    // If no keywords match, try to normalize the existing key
    if (planKey) {
      return this.normalizePlanKey(planKey);
    }
    
    // If we have a plan name but no keywords match, try to extract from name
    if (planName) {
      return this.normalizePlanKey(planName);
    }
    
    // Ultimate fallback
    return 'classic';
  }

  /**
   * Normalizes a plan key/name to a standard format
   */
  static normalizePlanKey(input) {
    if (!input) return 'classic';
    
    return input.toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/plan/g, '') // Remove "plan" word
      .replace(/_+/g, '_') // Collapse multiple underscores
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  }

  static updatePlanCards(currentPlanKey, planStatus) {
    const planCards = document.querySelectorAll('.membership-plan-card');
    
    planCards.forEach((card, index) => {
      const planElement = card.querySelector('[data-plan]');
      if (!planElement) return;

      const planKey = planElement.getAttribute('data-plan');
      const ctaButton = card.querySelector('.membership-plan-card__cta');
      const currentPlanDiv = card.querySelector('.membership-plan-card__current-plan');
      // First-box pricing doesn't apply to existing subscribers (one element per bottle size)
      if (planStatus != null) {
        card.querySelectorAll('.membership-plan-card__discount-price').forEach((discountPrice) => {
          discountPrice.style.display = 'none';
        });
      }

      // ✅ Check if this is the current plan AND current box configuration
      const isCurrentPlan = this.isPlanMatch(currentPlanKey, planKey) && planStatus === 'ACTIVE';

      const isCurrentBoxConfig = this.isCurrentBoxConfiguration();
      const isCurrent = isCurrentPlan && isCurrentBoxConfig;
      
      if (isCurrent) {
        if (currentPlanDiv) currentPlanDiv.style.display = 'block';
        if (ctaButton) {
          ctaButton.textContent = 'Current plan';
          ctaButton.disabled = true;
          ctaButton.setAttribute('aria-disabled', 'true');
        }
      } else {
        if (currentPlanDiv) currentPlanDiv.style.display = 'none';
        if (ctaButton) {
          const disabled = planStatus === 'UNAVAILABLE';
          ctaButton.textContent = disabled ? 'Unavailable' : 'Switch plan';
          ctaButton.className = `membership-plan-card__cta btn ${disabled ? 'secondary' : 'primary'}`;
          ctaButton.disabled = disabled;
          ctaButton.setAttribute('aria-disabled', String(disabled));
        }
      }
    });
  }

  static updateMembershipTable(currentPlanKey, planStatus) {
    const planHeaders = document.querySelectorAll('.membership-plans__table-plan-header');
    
    planHeaders.forEach((header, index) => {
      const planName = header.querySelector('.membership-plans__table-plan-name');
      const ctaButton = header.querySelector('.btn');
      const recommendedPill = header.querySelector('.membership-plans__table-plan-pill:not(.membership-plans__table-plan-pill--current)');
      if (!planName || !ctaButton) return;

      const planTitle = planName.textContent.trim();
      let planKey;
      if (planTitle.includes('Classic')) planKey = 'classic';
      else if (planTitle.includes('Preferred')) planKey = 'preferred';
      else if (planTitle.includes('Premier')) planKey = 'premier';
      else if (planTitle.includes('Fine Wine')) planKey = 'fine-wine';
      else planKey = planTitle.toLowerCase().replace(/\s+/g, '-').replace('plan', '').trim();
      
      // ✅ Check if this is the current plan AND current box configuration
      const isCurrentPlan = this.isPlanMatch(currentPlanKey, planKey) && planStatus === 'ACTIVE';
      const isCurrentBoxConfig = this.isCurrentBoxConfiguration();
      const isCurrent = isCurrentPlan && isCurrentBoxConfig;
      
      if (isCurrent) {
        if (recommendedPill) recommendedPill.style.display = 'none';
        ctaButton.textContent = 'Current plan';
        ctaButton.disabled = true;
        ctaButton.setAttribute('aria-disabled', 'true');
      } else {
        if (recommendedPill && planTitle.includes('Premier')) {
          recommendedPill.style.display = 'inline-block';
          recommendedPill.textContent = 'Recommended!';
        }
        const disabled = planStatus === 'UNAVAILABLE';
        ctaButton.textContent = disabled ? 'Unavailable' : 'Switch plan';
        ctaButton.className = `btn ${disabled ? 'secondary' : 'primary'}`;
        ctaButton.disabled = disabled;
        ctaButton.setAttribute('aria-disabled', String(disabled));
      }
    });
  }

  /**
   * Convert quiz links back to "Switch plan" buttons for users with subscriptions
   */
  static convertToSwitchButtons() {
    // Convert plan card links back to buttons
    document.querySelectorAll('.membership-plan-card__cta[href*="quiz"]').forEach(link => {
      const planKey = link.href.match(/pp=([^&]+)/)?.[1]?.replace('_plan', '').replace('_', '-');
      if (!planKey) return;
      
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'membership-plan-card__cta btn primary';
      button.setAttribute('data-plan', planKey);
      button.textContent = 'Switch plan';
      
      link.parentNode.replaceChild(button, link);
    });

    // Convert table links back to buttons
    document.querySelectorAll('.membership-plans__table-plan-header .btn[href*="quiz"]').forEach(link => {
      const planKey = link.href.match(/pp=([^&]+)/)?.[1]?.replace('_plan', '');
      if (!planKey) return;
      
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn primary';
      button.setAttribute('data-plan', planKey);
      button.textContent = 'Switch plan';
      
      link.parentNode.replaceChild(button, link);
    });
  }

  /**
   * Switch box configuration (6x1 to 12x1 or vice versa)
   */
  static async switchBoxConfiguration(customerId, boxConfig, clickedButton = null) {    
    if (!customerId || !boxConfig) {
      console.error('❌ Missing required parameters:', { customerId, boxConfig });
      return;
    }

    // Show loading spinner on clicked button
    if (clickedButton) {
      this.showButtonLoading(clickedButton);
    }

    try {
      const response = await this.fetchWithTimeout(`/a/sub/club-orders/preferences?logged_in_customer_id=${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent: 'updateBoxConfiguration',
          boxPattern: boxConfig
        })
      }, 10000);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Box configuration switch failed:', errorText);
        return;
      }

      const result = await response.json().catch(() => ({}));      
      if (result.error) {
        console.error('❌ Box configuration switch error:', result.error);
        return;
      }

      // Success!
      const currentData = await this.getCustomerSubscription(customerId, window.Shopify?.shop);
      const currentPlanKey = currentData ? this.getCurrentPlanKey(currentData) : 'classic';
      const currentPlanName = this.getPlanDisplayName(currentPlanKey);
      const boxSizeText = boxConfig === '6x1' ? '6 bottles' : '12 bottles';
      await this.handleSwitchSuccess(customerId, window.Shopify?.shop, currentPlanName, `${currentPlanName} (${boxSizeText})`, currentPlanKey, boxConfig);
      
    } catch (error) {
      console.error('�� Error switching box configuration:', error);
    } finally {
      // Hide loading spinner
      if (clickedButton) {
        this.hideButtonLoading(clickedButton);
      }
    }
  }

  /**
   * Switch plan — properly updates membership plan
   */
  static async switchPlan(planKey, customerId, shop, clickedButton = null) {
    if (!planKey || !customerId || !shop) {
      console.error('❌ Missing required parameters:', { planKey, customerId, shop });
      return;
    }
    const membershipPlanId = this.resolvePlanId(planKey, window.__SUBS_META__);
    
    if (!membershipPlanId) {
      console.error('❌ Invalid membershipPlanId:', membershipPlanId);
      return;
    }

    // Show loading spinner on clicked button
    if (clickedButton) {
      this.showButtonLoading(clickedButton);
    }

    try {
      // Get current plan info for success notification
      const currentData = await this.getCustomerSubscription(customerId, shop);
      const currentPlanKey = currentData ? this.getCurrentPlanKey(currentData) : 'classic';
      const currentPlanName = this.getPlanDisplayName(currentPlanKey);
      const newPlanName = this.getPlanDisplayName(planKey);
      
      // Get current box configuration
      const selectedToggle = document.querySelector('input[name="membership-plan"]:checked');
      const selectedSize = selectedToggle ? selectedToggle.value : '6-bottles';
      const boxConfig = selectedSize === '6-bottles' ? '6x1' : '12x1';

      // ✅ CORRECT API call with proper headers and URL
      const response = await this.fetchWithTimeout(`/a/sub/club-orders/membership-plan?logged_in_customer_id=${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          intent: 'UPDATE_MEMBERSHIP_PLAN',
          id: String(membershipPlanId),
          logged_in_customer_id: customerId
        })
      }, 10000);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Plan switch failed:', errorText);
        return;
      }

      const result = await response.json().catch(() => ({}));
      
      if (result.error) {
        console.error('❌ Plan switch error:', result.error);
        return;
      }

      // Success!
      await this.handleSwitchSuccess(customerId, shop, currentPlanName, newPlanName, planKey, boxConfig);
      
    } catch (error) {
      console.error('💥 Error switching plan:', error);
    } finally {
      // Hide loading spinner
      if (clickedButton) {
        this.hideButtonLoading(clickedButton);
      }
    }
  }

  /**
   * Switch both plan and box configuration using the same API as admin
   */
  static async switchPlanAndBoxConfiguration(planKey, boxConfig, customerId, shop, clickedButton = null) {
    
    if (!planKey || !boxConfig || !customerId || !shop) {
      console.error('❌ Missing required parameters:', { planKey, boxConfig, customerId, shop });
      return;
    }

    // Show loading spinner on clicked button
    if (clickedButton) {
      this.showButtonLoading(clickedButton);
    }

    try {
      // Get current plan info for success notification
      const currentData = await this.getCustomerSubscription(customerId, shop);
      const currentPlanKey = currentData ? this.getCurrentPlanKey(currentData) : 'classic';
      const currentPlanName = this.getPlanDisplayName(currentPlanKey);
      const newPlanName = this.getPlanDisplayName(planKey);
      const membershipPlanId = this.resolvePlanId(planKey, window.__SUBS_META__);
      
      if (!membershipPlanId) {
        console.error('❌ Invalid membershipPlanId:', membershipPlanId);
        return;
      }

      // Use existing endpoints - first update membership plan, then box configuration
      const planResponse = await this.fetchWithTimeout(`/a/sub/club-orders/membership-plan?logged_in_customer_id=${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          intent: 'UPDATE_MEMBERSHIP_PLAN',
          id: String(membershipPlanId)
        })
      }, 10000);

      if (!planResponse.ok) {
        const errorText = await planResponse.text();
        console.error('❌ Plan update failed:', errorText);
        return;
      }

      const boxResponse = await this.fetchWithTimeout(`/a/sub/club-orders/preferences?logged_in_customer_id=${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent: 'updateBoxConfiguration',
          boxPattern: boxConfig
        })
      }, 10000);

      if (!boxResponse.ok) {
        const errorText = await boxResponse.text();
        console.error('❌ Box configuration update failed:', errorText);
        return;
      }

      const response = boxResponse; // Use the last response for result processing
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Plan and box switch failed:', errorText);
        return;
      }

      const result = await response.json().catch(() => ({}));      
      if (result.error) {
        console.error('❌ Plan and box switch error:', result.error);
        return;
      }

      // Success! Clear cache and refresh UI
      const boxSizeText = boxConfig === '6x1' ? '6 bottles' : '12 bottles';
      await this.handleSwitchSuccess(customerId, shop, currentPlanName, `${newPlanName} (${boxSizeText})`, planKey, boxConfig);
      
    } catch (error) {
      console.error('💥 Error switching plan and box configuration:', error);
    } finally {
      // Hide loading spinner
      if (clickedButton) {
        this.hideButtonLoading(clickedButton);
      }
    }
  }

  // --- util pieces
  /**
   * Flexible plan matching using keyword analysis
   */
  static isPlanMatch(currentPlanKey, targetPlanKey) {
    if (!currentPlanKey || !targetPlanKey) return false;
    
    // Direct match
    if (currentPlanKey === targetPlanKey) return true;
    
    // Use the same keyword detection logic to normalize both keys
    const normalizedCurrent = this.determinePlanKeyFromKeywords(currentPlanKey, currentPlanKey);
    const normalizedTarget = this.determinePlanKeyFromKeywords(targetPlanKey, targetPlanKey);
    
    // Check if they resolve to the same plan type
    if (normalizedCurrent === normalizedTarget) return true;
    
    // Also check if either key contains the other (for partial matches)
    const planPatterns = this.getPlanPatterns();
    
    // Check if both keys contain keywords from the same plan type
    for (const [planType, keywords] of Object.entries(planPatterns)) {
      const currentHasKeywords = keywords.some(keyword => currentPlanKey.toLowerCase().includes(keyword));
      const targetHasKeywords = keywords.some(keyword => targetPlanKey.toLowerCase().includes(keyword));
      
      if (currentHasKeywords && targetHasKeywords) {
        return true;
      }
    }
    
    return false;
  }

  static resolvePlanId(planKey, planMeta) {
    // First try to find from membership plans data (from fetchMembershipPlans)
    if (window.MembershipPlansData && Array.isArray(window.MembershipPlansData)) {
      const plan = window.MembershipPlansData.find(p => {
        return this.isPlanMatch(planKey, p.plan_slug) || 
               this.isPlanMatch(planKey, p.plan_name?.toLowerCase().replace(/\s+/g, '_'));
      });
      if (plan && plan.plan_id) {
        return plan.plan_id;
      }
    }
    
    // Try planMeta if available
    if (planMeta?.availablePlans) {
      const hit = planMeta.availablePlans.find(p => this.isPlanMatch(planKey, p.key));
      if (hit) return hit.id;
    }
    
    // Try DOM elements
    const el = document.querySelector(`[data-plan="${planKey}"][data-plan-id]`);
    if (el) return Number(el.getAttribute('data-plan-id'));
    
    // Fallback mapping
    const fallback = { 
      classic: 1, 
      preferred: 2, 
      premier: 3, 
      'fine-wine': 4,
      'fine_wine': 4,
      'fine_wine_plan': 4
    };
    return fallback[planKey];
  }

  static isCurrentBoxConfiguration() {
    // Check if the current toggle selection matches the user's actual box configuration
    const selectedToggle = document.querySelector('input[name="membership-plan"]:checked');
    const selectedSize = selectedToggle ? selectedToggle.value : '6-bottles';
    
    // Map toggle values to box configurations
    const sizeToBoxConfig = {
      '6-bottles': '6x1',
      '12-bottles': '12x1'
    };
    
    const currentToggleBoxConfig = sizeToBoxConfig[selectedSize];
    const isMatch = this.currentBoxConfiguration === currentToggleBoxConfig;
  
    
    return isMatch;
  }

  static clearCache(customerId = null) {
    if (customerId) {
      const shop = window.Shopify?.shop;
      if (shop) delete this.cache[`${shop}-${customerId}`];
    } else {
      this.cache = {};
    }
  }
  /**
   * Show loading spinner on button
   */
  static showButtonLoading(button) {
    if (!button) return;
    
    const originalText = button.textContent;
    button.setAttribute('data-original-text', originalText);
    button.classList.add('btn--loading');
    button.disabled = true;
    
    // Clear the button content and add loading spinner
    button.innerHTML = '';
    
    // Add loading spinner
    const spinner = document.createElement('div');
    spinner.className = 'loading__spinner';
    spinner.innerHTML = `
      <svg class="loading__spinner-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="50.27" stroke-dashoffset="50.27">
          <animate attributeName="stroke-dasharray" dur="2s" values="0 50.27;25.13 25.13;0 50.27" repeatCount="indefinite"/>
          <animate attributeName="stroke-dashoffset" dur="2s" values="0;-25.13;-50.27" repeatCount="indefinite"/>
        </circle>
      </svg>
    `;
    button.appendChild(spinner);
  }

  /**
   * Hide loading spinner on button
   */
  static hideButtonLoading(button) {
    if (!button) return;
    
    button.classList.remove('btn--loading');
    button.disabled = false;
    
    // Remove spinner
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.remove();
    
    // Restore original text
    const originalText = button.getAttribute('data-original-text');
    if (originalText) {
      button.textContent = originalText;
      button.removeAttribute('data-original-text');
    }
  }

  /**
   * Show success notification with plan details - fetches section and updates data
   */
  static async showPlanSwitchSuccess(fromPlan, toPlan, planDetails, nextShipmentDate = null) {
    console.log('🎉 Showing plan switch success:', { fromPlan, toPlan, planDetails });
    
    // Find the MainContent container
    const mainContent = document.getElementById('MainContent');
    
    if (!mainContent) {
      console.error('❌ MainContent element not found');
      console.warn('Success! Plan switched successfully.', 'success');
      return;
    }

    try {
      console.log('📡 Fetching success section using Section Rendering API...');
      
      const sectionUrl = `${window.location.pathname}?sections=plan-switch-success`;
      console.log('📡 Fetching from:', sectionUrl);
      
      const response = await fetch(sectionUrl);
      console.log('📡 Response status:', response.status, response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📡 Response data keys:', Object.keys(data));
      
      // Extract the section HTML from the JSON response
      const sectionHTML = data['plan-switch-success'];
      
      if (!sectionHTML) {
        throw new Error('Section "plan-switch-success" not found in response');
      }
      
      console.log('📡 Section HTML length:', sectionHTML.length);
      
      // Replace the MainContent with the fetched section
      mainContent.innerHTML = sectionHTML;
      console.log('✅ Section loaded successfully');
      
      // Update the dynamic data
      this.updateSuccessSectionData(fromPlan, toPlan, planDetails, nextShipmentDate);
      console.log('✅ Data updated successfully');
      
      // Scroll to top
      window.scrollTo(0, 0);
      
    } catch (error) {
      console.error('❌ Error loading success section:', error);
      console.warn('Success! Plan switched successfully.', 'success');
    }
  }


  /**
   * Update the success section with dynamic data
   */
  static updateSuccessSectionData(fromPlan, toPlan, planDetails, nextShipmentDate = null) {
    // Add URL to history so user can navigate back to membership plans page
    const membershipPlansUrl = '/pages/membership-plans';
    if (window.history && window.history.pushState) {
      window.history.pushState(
        { page: 'plan-switch-success', fromPage: 'membership-plans' },
        'Plan Switch Success',
        membershipPlansUrl + '#success'
      );
      
      // Add popstate listener to handle back button
      window.addEventListener('popstate', this.handlePopState.bind(this));
    }

    // Update plan names
    const fromPlanElements = document.querySelectorAll('[data-from-plan]');
    fromPlanElements.forEach(el => el.textContent = fromPlan);
    
    const toPlanElements = document.querySelectorAll('[data-to-plan]');
    toPlanElements.forEach(el => el.textContent = toPlan);
    
    const toPlanNameElements = document.querySelectorAll('[data-to-plan-name]');
    toPlanNameElements.forEach(el => el.textContent = toPlan);
    
    // Update message
    const messageElement = document.querySelector('[data-message]');
    if (messageElement) {
      const message = nextShipmentDate 
        ? `You have upgraded your plan from ${fromPlan} to ${toPlan} and will be receiving your next club shipment on ${nextShipmentDate}.`
        : `You have upgraded your plan from ${fromPlan} to ${toPlan}.`;
      messageElement.textContent = message;
    }
    
    // Update CTA description
    const ctaDescriptionElement = document.querySelector('[data-cta-description]');
    if (ctaDescriptionElement) {
      ctaDescriptionElement.innerHTML = `Take a look at how your wines have changed after switching to the <span data-to-plan-name>${toPlan}</span> plan.`;
    }
    
    // Update plan details
    const planNameElement = document.querySelector('[data-plan-name]');
    if (planNameElement) planNameElement.textContent = planDetails.name;
    
    const planDescriptionElement = document.querySelector('[data-plan-description]');
    if (planDescriptionElement) planDescriptionElement.textContent = planDetails.description;
    
    const planPriceElement = document.querySelector('[data-plan-price]');
    if (planPriceElement) planPriceElement.textContent = planDetails.price;
    
    const planRetailValueElement = document.querySelector('[data-plan-retail-value]');
    if (planRetailValueElement) planRetailValueElement.textContent = `Min. retail value of ${planDetails.retailValue}`;
    
    // Update benefits
    const benefitsListElement = document.querySelector('[data-plan-benefits]');
    if (benefitsListElement) {
      // Get the first existing li to copy its structure
      const existingLi = benefitsListElement.querySelector('li');
      if (existingLi) {
        const existingImg = existingLi.querySelector('img');
        const imgHtml = existingImg ? existingImg.outerHTML : '';
        
        benefitsListElement.innerHTML = planDetails.benefits.map(benefit => `
          <li>
            ${imgHtml}
            <span>${benefit}</span>
          </li>
        `).join('');
      }
    }
  }

  /**
   * Handle browser back button navigation
   */
  static handlePopState(event) {
    // If we're going back from the success page, reload the membership plans page
    if (event.state && event.state.fromPage === 'membership-plans') {
      window.location.reload();
    }
  }

  /**
   * Get plan display name from plan key
   */
  static getPlanDisplayName(planKey) {
    const planNames = {
      'classic': 'Classic',
      'preferred': 'Preferred',
      'premier': 'Premier',
      'fine-wine': 'Fine Wine',
      'fine_wine': 'Fine Wine'
    };
    return planNames[planKey] || 'Classic';
  }

  /**
   * Common success handling for plan switches
   */
  static async handleSwitchSuccess(customerId, shop, fromPlan, toPlan, planKey, boxConfig) {
    this.clearCache(`${customerId}`);
    const updatedData = await this.getCustomerSubscription(customerId, shop);
    this.updateMembershipPlanCTAs(updatedData);

    const planDetails = this.getPlanDetails(planKey, boxConfig);
    this.showPlanSwitchSuccess(fromPlan, toPlan, planDetails);
  }

  /**
   * Get plan details for success notification
   */
  static getPlanDetails(planKey, boxConfig = '6x1') {
    const is12Bottles = boxConfig === '12x1';
    const sizeKey = is12Bottles ? '12-bottles' : '6-bottles';

    // Get pricing from settings if available
    const pricing = window.MembershipPlansSettings?.[planKey]?.[sizeKey];

    // Helper function to ensure price shows "/club order" instead of "plus shipping"
    const formatPrice = (priceString) => {
      if (!priceString) return null;
      return priceString.replace(/plus shipping/gi, '/ club order');
    };

    const planDetails = {
      classic: {
        name: 'Classic Plan',
        description: 'A unique 6-bottle selection of quality wine at an amazing price.',
        price: formatPrice(pricing?.fullPrice) || (is12Bottles ? '$199 / club order' : '$99 / club order'),
        retailValue: pricing?.retailValue || (is12Bottles ? '$277' : '$138'),
        benefits: [
          `<b>Save at least ${pricing?.savings || (is12Bottles ? '$78' : '$39')}</b> per club order`,
          `Average retail price of <b>at least $23/bottle</b> (You only pay $16.50/bottle)`,
          'Satisfaction guarantee',
        ],
      },
      preferred: {
        name: 'Preferred Plan',
        description: 'A select 6-bottle offering of noteworthy wine with incredible savings.',
        price: formatPrice(pricing?.fullPrice) || (is12Bottles ? '$229 /club order' : '$114 /club order'),
        retailValue: pricing?.retailValue || (is12Bottles ? '$319' : '$159'),
        benefits: [
          `<b>Save at least ${pricing?.savings || (is12Bottles ? '$90' : '$45')}</b> per club order`,
          `Average retail price of <b>at least $26.50/bottle</b> (You only pay $19/bottle)`,
          'Satisfaction guarantee',
        ],
      },
      premier: {
        name: 'Premier Plan',
        description: 'A top-tier 6-bottle collection of world-class wine at our best value.',
        price: formatPrice(pricing?.fullPrice) || (is12Bottles ? '$259 /club order' : '$129 /club order'),
        retailValue: pricing?.retailValue || (is12Bottles ? '$361' : '$180'),
        benefits: [
          `<b>Save at least ${pricing?.savings || (is12Bottles ? '$102' : '$51')}</b> per club order`,
          `Average retail price of <b>at least $28.50/bottle</b> (You only pay $21.50/bottle)`,
          '+1 Fine Wine Collection bottle included in every order',
          'Satisfaction guarantee',
        ],
      },
      'fine-wine': {
        name: 'Fine Wine Plan',
        description: 'An exceptional 6-bottle selection from our Fine Wine Collection at accessible prices.',
        price: formatPrice(pricing?.fullPrice) || (is12Bottles ? '$319 /club order' : '$159 /club order'),
        retailValue: pricing?.retailValue || (is12Bottles ? '$447' : '$223'),
        benefits: [
          `<b>Save at least ${pricing?.savings || (is12Bottles ? '$128' : '$64')}</b> per club order`,
          `Average retail price of <b>at least $37.16/bottle</b> (You only pay $26.50/bottle)`,
          'All (6) Fine Wine Collection bottles in every order',
          'Satisfaction guarantee',
        ],
      },
    };

    return planDetails[planKey] || planDetails.classic;
  }

  static addSwitchPlanHandlers(customerId, shop) {
    // Handle plan switching (Classic, Preferred, Premier, etc.)
    const planCardButtons = document.querySelectorAll('.membership-plan-card__cta[data-plan]');
    const tableButtons = document.querySelectorAll('.membership-plans__table-plan-header .btn[data-plan]');
    
    // Combine both button types and add the same handler
    const allPlanButtons = [...planCardButtons, ...tableButtons];
    
    allPlanButtons.forEach((button) => {
      const planKey = button.getAttribute('data-plan');
      button.addEventListener('click', (e) => {
        e.preventDefault();
        if (planKey) {
          this.handlePlanSwitch(planKey, customerId, shop, button);
        }
      });
    });

    // ✅ Toggle should sync with subscription data and update UI state
    const toggleInputs = document.querySelectorAll('input[name="membership-plan"]');
    toggleInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.refreshSubscriptionState(customerId, shop);
      });
    });
  }

  /**
   * Centralized plan switching logic to eliminate duplication
   */
  static handlePlanSwitch(planKey, customerId, shop, clickedButton = null) {
    // Get the current box configuration from the toggle
    const selectedToggle = document.querySelector('input[name="membership-plan"]:checked');
    const selectedSize = selectedToggle ? selectedToggle.value : '6-bottles';
    const boxConfig = selectedSize === '6-bottles' ? '6x1' : '12x1';
              
    // Check if we need to change both plan and box configuration
    const currentData = this.getCustomerSubscription(customerId, shop);
    if (currentData) {
      currentData.then((data) => {
        if (data) {
          const currentPlanKey = this.getCurrentPlanKey(data);
          const currentBoxConfig = data.boxConfiguration;
          
          const planNeedsChange = planKey !== currentPlanKey;
          const boxNeedsChange = boxConfig !== currentBoxConfig;
          
          if (planNeedsChange && boxNeedsChange) {
            // Both plan and box need to change - use the combined approach
            this.switchPlanAndBoxConfiguration(planKey, boxConfig, customerId, shop, clickedButton);
          } else if (planNeedsChange) {
            // Only plan needs to change
            this.switchPlan(planKey, customerId, shop, clickedButton);
          } else if (boxNeedsChange) {
            // Only box configuration needs to change - use the simpler endpoint
            this.switchBoxConfiguration(customerId, boxConfig, clickedButton);
          }
        }
      });
    }
  }

  static getCurrentPlanKey(planData) {
    const planInfo = this.extractPlanInfoFromData(planData);
    return planInfo.planKey;
  }

  /**
   * Refresh subscription state display when toggle changes
   */
  static async refreshSubscriptionState(customerId, shop) {
    
    try {
      const subscriptionData = await this.getCustomerSubscription(customerId, shop);
      if (subscriptionData) {
        this.updateMembershipPlanCTAs(subscriptionData);
      }
    } catch (error) {
      console.error('💥 Error refreshing subscription state:', error);
    }
  }

  /**
   * Fetch membership plans data and update DOM with plan IDs
   * This runs first to ensure data is available for other operations
   */
  static async fetchMembershipPlans() {
    try {
      const response = await this.fetchWithTimeout('/a/sub/membership-plans');
      const data = await response.json();
      if (data.data) {
        window.MembershipPlansData = data.data;
        console.log('✅ Membership plans data loaded:', window.MembershipPlansData);
        this.updatePlanIds();
      }
    } catch (error) {
      console.warn('⚠️ Error fetching membership plans:', error);
    }
  }

  /**
   * Update plan IDs in the DOM based on fetched membership plans data
   */
  static updatePlanIds() {
    if (!window.MembershipPlansData) return;
    
    // Update plan IDs in the membership plan cards
    window.MembershipPlansData.forEach(plan => {
      const planKey = plan.plan_slug; // classic, preferred, premier, fine-wine
      const planId = plan.plan_id;
      
      // Update data-plan-id attributes
      const planCards = document.querySelectorAll(`[data-plan="${planKey}"]`);
      planCards.forEach(card => {
        const ctaButton = card.querySelector('.membership-plan-card__cta');
        if (ctaButton) {
          ctaButton.setAttribute('data-plan-id', planId);
        }
      });
    });
  }

  static async init() {
    // First, fetch membership plans data
    await this.fetchMembershipPlans();
    
    const customerId = window.Shopify?.customer?.id;
    const shop = window.Shopify?.shop;
    
    if (!customerId || !shop) {
      console.log('👤 No customer logged in, skipping subscription service initialization');
      return;
    }

    try {
      const subscriptionData = await this.getCustomerSubscription(customerId, shop);
      this.updateMembershipPlanCTAs(subscriptionData);
      this.addSwitchPlanHandlers(customerId, shop);
    } catch (error) {
      console.error('Init error:', error);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  CustomerSubscriptionService.init();
});
window.CustomerSubscriptionService = CustomerSubscriptionService;